import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Trophy, Clock, BookOpen, Play, CheckCircle, Timer, Eye } from 'lucide-react';
import { Test } from '../types/test';
import { testService } from '../services/testService';
import { testResultService } from '../services/testResultService';
import RecentActivities from '../components/dashboard/RecentActivities';
import { StudentAccessBanner } from '../components/StudentAccessBanner';
import { formatWithTimezone } from '../utils/dateUtils';
import { TimezoneIndicator } from '../components/TimezoneIndicator';

interface CompletedTestWithResult extends Test {
  resultId: string;
  percentage: number;
  score: number;
  maxScore: number;
  attemptNumber?: number;
  totalAttempts: number;
}

const HomePage: React.FC = () => {
  const [upcomingTests, setUpcomingTests] = useState<Test[]>([]);
  const [completedTests, setCompletedTests] = useState<CompletedTestWithResult[]>([]);
  const [uniqueCompletedTestsCount, setUniqueCompletedTestsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadTests();
    // Refresh every minute to update test availability
    const interval = setInterval(loadTests, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadTests = async () => {
    try {
      const [upcoming, completed] = await Promise.all([
        testService.getUpcomingTests(),
        testService.getCompletedTests()
      ]);
      setUpcomingTests(upcoming);

      // For completed tests, fetch ALL their results to show multiple attempts
      const completedWithResults: CompletedTestWithResult[] = [];
      for (const test of completed) {
        try {
          // Get all attempts for this test, not just the latest
          const allAttempts = await testResultService.getAllTestAttempts(test.id);

          // Add each attempt as a separate record
          allAttempts.forEach((result, index) => {
            completedWithResults.push({
              ...test,
              resultId: result.id,
              percentage: result.percentage || 0,
              score: result.score || 0,
              maxScore: result.maxScore || 0,
              attemptNumber: allAttempts.length > 1 ? index + 1 : undefined, // Only show attempt number if > 1
              totalAttempts: allAttempts.length
            });
          });
        } catch (error) {
          console.warn(`Failed to fetch results for test ${test.id}:`, error);
          // Still include the test but with default values
          completedWithResults.push({
            ...test,
            resultId: test.id, // Use test ID as fallback
            percentage: 0,
            score: 0,
            maxScore: 0,
            attemptNumber: undefined,
            totalAttempts: 1
          });
        }
      }

      setCompletedTests(completedWithResults);
      // Set unique completed tests count (number of unique tests, not attempts)
      setUniqueCompletedTestsCount(completed.length);
    } catch (error) {
      console.error('Failed to load tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = async (test: Test) => {
    console.log('🚀 handleStartTest called with test:', test);
    console.log('🔍 Test status:', test.status);
    console.log('🔍 Test canViewInstructions:', test.canViewInstructions);
    console.log('🔍 Test canStartTest:', test.canStartTest);

    // Handle completed tests first
    if (test.status === 'completed') {
      console.log('✅ Test is completed, navigating to results');
      navigate(`/results/${test.id}`);
      return;
    }

    // Handle expired tests - backend determines expiration
    if (test.status === 'expired') {
      console.log('⏰ Test schedule has expired');
      alert('This test schedule has expired and can no longer be taken.');
      return;
    }

    // ALWAYS SHOW INSTRUCTIONS FIRST - This is the desired UX
    // Show instructions for ALL tests before they start (regardless of status or backend flags)
    // This ensures students always see instructions before every test, even in-progress ones
    console.log('🎯 Checking navigation conditions...');
    console.log('🎯 canViewInstructions:', test.canViewInstructions);
    console.log('🎯 canStartTest:', test.canStartTest);

    if (test.canViewInstructions || test.canStartTest) {
      console.log('🎯 NAVIGATING TO INSTRUCTIONS PAGE:', `/test/${test.id}/instructions`);
      navigate(`/test/${test.id}/instructions`);
      return;
    }

    // If neither is available, show error
    console.log('❌ Test is not available');
    alert('This test is not available at this time.');
  };

  const parseServerDate = (dateString: string): Date => {
    // Treat naive timestamps as local time to avoid timezone shifts
    // ISO with 'Z' or offset will be parsed accordingly by Date
    return new Date(dateString);
  };

  const getTimeUntilStart = (test: Test): string => {
    const now = new Date();
    // Parse dates assuming they are UTC if no timezone is specified
    const startTime = parseServerDate(test.scheduledStartTime);
    const scheduleEndTime = parseServerDate(test.scheduledEndTime);
    const allowedTime = parseServerDate(test.allowedStartTime || test.scheduledStartTime);

    // Determine if this is an on-demand test (heuristic: if allowedStartTime is significantly before scheduledStartTime)
    const isOnDemandTest = allowedTime.getTime() < (startTime.getTime() - (6 * 60 * 60 * 1000)); // More than 6 hours difference

    // Don't show 'Expired' for in-progress tests - backend handles status
    if (test.status === 'in-progress') {
      return 'In Progress';
    }

    // Check if test schedule has expired (only for not-started tests)
    if (now > scheduleEndTime) {
      return 'Expired';
    } else if (isOnDemandTest) {
      // For on-demand tests, show availability window
      if (now >= allowedTime) {
        return 'Available Now';
      } else {
        const diffMs = allowedTime.getTime() - now.getTime();
        const hours = Math.ceil(diffMs / (1000 * 60 * 60));
        const minutes = Math.ceil(diffMs / (1000 * 60));

        if (hours >= 1) {
          return `Available in ${hours}h`;
        } else if (minutes > 0) {
          return `Available in ${minutes}m`;
        } else {
          return 'Available Now';
        }
      }
    } else {
      // For scheduled tests, show precise timing
      if (now >= startTime) {
        return 'Available Now';
      } else if (now >= allowedTime) {
        const diffMs = startTime.getTime() - now.getTime();
        const totalMinutes = Math.max(0, Math.round(diffMs / (1000 * 60)));
        if (totalMinutes >= 60) {
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          return minutes > 0 ? `Starts in ${hours}h ${minutes}m` : `Starts in ${hours}h`;
        }
        return `Starts in ${totalMinutes}m`;
      } else {
        const diffMs = allowedTime.getTime() - now.getTime();
        const totalMinutes = Math.max(0, Math.round(diffMs / (1000 * 60)));
        if (totalMinutes >= 60) {
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          return minutes > 0 ? `Available in ${hours}h ${minutes}m` : `Available in ${hours}h`;
        }
        return totalMinutes > 0 ? `Available in ${totalMinutes}m` : 'Available Now';
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center bg-card rounded-xl p-8 border border-border">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-border border-t-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground text-base font-medium font-body">Loading your tests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Access Status Banner */}
        <StudentAccessBanner />

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3 font-heading">Welcome to BayesPrep Student Portal!</h1>
          <p className="text-lg sm:text-xl text-muted-foreground font-body">
            Ready to excel in your next test?
          </p>
          <div className="mt-2 flex justify-center">
            <TimezoneIndicator />
          </div>
        </div>

        {/* Performance Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-card border border-border p-4 transition-all duration-200 hover:scale-105">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-primary rounded-xl">
                <Calendar className="h-5 w-5 text-primary-foreground" />
              </div>
              <div>
                <p className="text-sm font-semibold text-muted-foreground font-body">Upcoming</p>
                <p className="text-xl sm:text-2xl font-bold text-foreground font-heading">{upcomingTests.length}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-card border border-border p-4 transition-all duration-200 hover:scale-105">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-bayesprep-green rounded-xl">
                <Trophy className="h-5 w-5 text-black" />
              </div>
              <div>
                <p className="text-sm font-semibold text-muted-foreground font-body">Completed</p>
                <p className="text-xl sm:text-2xl font-bold text-foreground font-heading">{uniqueCompletedTestsCount}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-card border border-border p-4 transition-all duration-200 hover:scale-105">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-bayesprep-yellow rounded-xl">
                <Clock className="h-5 w-5 text-black" />
              </div>
              <div>
                <p className="text-sm font-semibold text-muted-foreground font-body">Avg Score</p>
                <p className="text-xl sm:text-2xl font-bold text-foreground font-heading">
                  {completedTests.length > 0
                    ? Math.round(completedTests.reduce((sum, test) => sum + (test.percentage || 0), 0) / completedTests.length)
                    : 0}%
                </p>
              </div>
            </div>
          </Card>

          <Card className="bg-card border border-border p-4 transition-all duration-200 hover:scale-105">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-primary rounded-xl">
                <BookOpen className="h-5 w-5 text-primary-foreground" />
              </div>
              <div>
                <p className="text-sm font-semibold text-muted-foreground font-body">Total Tests</p>
                <p className="text-xl sm:text-2xl font-bold text-foreground font-heading">{upcomingTests.length + uniqueCompletedTestsCount}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Upcoming Tests */}
        <Card className="bg-card border border-border mb-6">
          <CardHeader className="pb-4 bg-primary/10 rounded-t-xl">
            <CardTitle className="text-xl flex items-center font-semibold text-foreground font-heading">
              <Timer className="h-6 w-6 mr-3 text-primary" />
              Upcoming Tests
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-6">
            {upcomingTests.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="h-8 w-8 text-primary" />
                </div>
                <p className="text-muted-foreground font-medium font-body">No upcoming tests scheduled</p>
              </div>
            ) : (
              upcomingTests.map((test) => (
                <div
                  key={test.id}
                  className="bg-card border border-border rounded-xl p-4 transition-all duration-200 transform hover:scale-[1.02]"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-foreground font-heading">{test.title}</h3>
                      <p className="text-sm text-muted-foreground font-medium font-body">{test.subject}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-bold ${
                        test.status === 'in-progress' ? 'text-orange-600' : 
                        test.status === 'expired' ? 'text-red-600' : 
                        'text-green-600'
                      }`}>
                        {test.status === 'in-progress' ? 'In Progress' : getTimeUntilStart(test)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatWithTimezone(test.scheduledStartTime)}
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-6 text-sm text-muted-foreground">
                      <span className="flex items-center font-medium font-body">
                        <Clock className="h-4 w-4 mr-2" />
                        {test.duration} min
                      </span>
                      <span className="flex items-center font-medium font-body">
                        <BookOpen className="h-4 w-4 mr-2" />
                        {test.totalQuestions} questions
                      </span>
                    </div>

                    {test.status === 'completed' ? (
                      <Button
                        size="sm"
                        onClick={() => navigate(`/results/${test.id}`)}
                        className="px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-200 transform hover:scale-105 bg-green-600 text-white hover:bg-green-700"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        View Results
                      </Button>
                    ) : test.status === 'in-progress' || test.canResume ? (
                      <Button
                        size="sm"
                        onClick={() => navigate(`/test/${test.id}`)}
                        className="px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-200 transform hover:scale-105 bg-orange-600 text-white hover:bg-orange-700"
                      >
                        <Play className="h-4 w-4 mr-2" />
                        Resume Test
                      </Button>
                    ) : test.status === 'expired' ? (
                      <Button
                        size="sm"
                        disabled
                        className="px-4 py-2 rounded-xl font-semibold text-sm bg-red-100 text-red-600 cursor-not-allowed"
                      >
                        <Clock className="h-4 w-4 mr-2" />
                        Schedule Expired
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => {
                          console.log('Button clicked for test:', test);
                          console.log('test.canStart:', test.canStart);
                          console.log('Button disabled:', !test.canStart);
                          handleStartTest(test);
                        }}
                        disabled={!test.canViewInstructions && !test.canStartTest}
                        className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-200 transform hover:scale-105 ${
                          test.canStartTest
                            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                            : test.canViewInstructions
                            ? 'bg-green-500 text-white hover:bg-green-600'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {test.canStartTest ? (
                          <>
                            <Play className="h-4 w-4 mr-2" />
                            Start Test
                          </>
                        ) : test.canViewInstructions ? (
                          <>
                            <Eye className="h-4 w-4 mr-2" />
                            View Instructions
                          </>
                        ) : (
                          'Not Available'
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <div className="lg:col-span-2">
            <Card className="bg-card border border-border">
              <CardHeader className="pb-4 bg-blue-50 rounded-t-xl">
                <CardTitle className="text-xl flex items-center font-semibold text-foreground font-heading">
                  <BookOpen className="h-6 w-6 mr-3 text-blue-600" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Link to="/tests">
                    <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold py-4 rounded-xl transition-all duration-200 transform hover:scale-105">
                      <Play className="h-5 w-5 mr-2" />
                      Take a Test
                    </Button>
                  </Link>
                  <Link to="/results">
                    <Button variant="secondary" className="w-full font-semibold py-4 rounded-xl transition-all duration-200 transform hover:scale-105">
                      <CheckCircle className="h-5 w-5 mr-2" />
                      View Results
                    </Button>
                  </Link>
                </div>

                {completedTests.length > 0 && (
                  <div className="mt-6 p-4 bg-bayesprep-green/20 rounded-xl border border-bayesprep-green/40">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-800">Latest Performance</h4>
                        <p className="text-sm text-gray-600">
                          Last test: {Math.round(completedTests[0]?.percentage || 0)}% score
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-800">
                          {completedTests.length > 0
                            ? Math.round(completedTests.reduce((sum, test) => sum + (test.percentage || 0), 0) / completedTests.length)
                            : 0}%
                        </p>
                        <p className="text-xs text-gray-600">Average</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Activities */}
          <div className="lg:col-span-1">
            <RecentActivities limit={8} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
